import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { User, Mail, Lock, Phone, CheckCircle, AlertCircle } from 'lucide-react';
import './SellerSignup.css'; // Reuse some styles for consistency

const AdminSetup = () => {
    const [formData, setFormData] = useState({
        username: '',
        email: '',
        password: '',
        phone: '',
        first_name: '',
        last_name: ''
    });
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState({ type: '', text: '' });
    const navigate = useNavigate();

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setMessage({ type: '', text: '' });

        const apiUrl = import.meta.env.VITE_API_URL || 'https://autoboyexpress.com/api/v1';

        try {
            const response = await fetch(`${apiUrl}/auth/setup-admin`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(formData)
            });

            const data = await response.json();

            if (response.ok) {
                setMessage({ type: 'success', text: data.message || 'Admin account created successfully!' });
                setTimeout(() => navigate('/login'), 3000);
            } else {
                setMessage({ 
                    type: 'error', 
                    text: data.message || 'Failed to create admin account. Setup may already be complete.' 
                });
            }
        } catch (error) {
            setMessage({ 
                type: 'error', 
                text: 'Network error. Please try again later.' 
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="seller-signup-container">
            <div className="signup-card" style={{ maxWidth: '600px', margin: '40px auto' }}>
                <div className="signup-header">
                    <img src="/autoboy_logo2.png" alt="AutoBoy Logo" className="signup-logo" style={{ maxWidth: '150px', marginBottom: '20px' }} />
                    <h1>Initial Admin Setup</h1>
                    <p>Create the first administrator account for AutoBoyExpress</p>
                </div>

                {message.text && (
                    <div className={`message-banner ${message.type}`} style={{ 
                        padding: '15px', 
                        borderRadius: '8px', 
                        marginBottom: '20px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px',
                        backgroundColor: message.type === 'success' ? '#DEF7EC' : '#FDE8E8',
                        color: message.type === 'success' ? '#03543F' : '#9B1C1C',
                        border: `1px solid ${message.type === 'success' ? '#BCF0DA' : '#F8B4B4'}`
                    }}>
                        {message.type === 'success' ? <CheckCircle size={20} /> : <AlertCircle size={20} />}
                        {message.text}
                    </div>
                )}

                <form onSubmit={handleSubmit} className="signup-form">
                    <div className="form-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                        <div className="form-group">
                            <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}><User size={16} /> First Name</label>
                            <input
                                type="text"
                                name="first_name"
                                value={formData.first_name}
                                onChange={handleChange}
                                required
                                placeholder="John"
                            />
                        </div>
                        <div className="form-group">
                            <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}><User size={16} /> Last Name</label>
                            <input
                                type="text"
                                name="last_name"
                                value={formData.last_name}
                                onChange={handleChange}
                                required
                                placeholder="Doe"
                            />
                        </div>
                    </div>

                    <div className="form-group">
                        <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}><Mail size={16} /> Email Address</label>
                        <input
                            type="email"
                            name="email"
                            value={formData.email}
                            onChange={handleChange}
                            required
                            placeholder="admin@autoboyexpress.com"
                        />
                    </div>

                    <div className="form-group">
                        <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}><User size={16} /> Username</label>
                        <input
                            type="text"
                            name="username"
                            value={formData.username}
                            onChange={handleChange}
                            required
                            placeholder="superadmin"
                        />
                    </div>

                    <div className="form-group">
                        <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}><Phone size={16} /> Phone Number</label>
                        <input
                            type="tel"
                            name="phone"
                            value={formData.phone}
                            onChange={handleChange}
                            required
                            placeholder="08012345678"
                        />
                    </div>

                    <div className="form-group">
                        <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}><Lock size={16} /> Password</label>
                        <input
                            type="password"
                            name="password"
                            value={formData.password}
                            onChange={handleChange}
                            required
                            placeholder="Min 8 chars, mixed case, symbols"
                        />
                    </div>

                    <button type="submit" className="submit-btn" disabled={loading} style={{ 
                        width: '100%', 
                        padding: '15px', 
                        background: '#22C55E', 
                        color: 'white', 
                        border: 'none', 
                        borderRadius: '8px', 
                        fontSize: '16px', 
                        fontWeight: 'bold', 
                        cursor: 'pointer',
                        marginTop: '20px',
                        transition: 'opacity 0.2s',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center'
                    }}>
                        {loading ? 'Creating Admin...' : 'Create Admin Account'}
                    </button>
                </form>

                <div className="signup-footer" style={{ marginTop: '30px', textAlign: 'center', color: '#666', fontSize: '14px' }}>
                    <p>Security Notice: This tool will be disabled once the first admin is created.</p>
                </div>
            </div>
        </div>
    );
};

export default AdminSetup;
